import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { IntlProvider, useIntl } from 'react-intl';
import Dutch from './locales/nl.json';
import English from './locales/en.json';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import InstallPrompt from './components/InstallPrompt';
// Deze 2 publieke, klant-gerichte pagina's blijven bewust EAGER (niet lazy)
// i.p.v. de rest hieronder — het zijn de meest bezochte, merk-kritieke
// pagina's (een klant boekt, beheert of geeft feedback, nooit ingelogd) en
// mogen geen extra, opeenvolgende netwerk-ronde kosten (main.js downloaden
// → PAS DAN een 2e bestand ophalen) bovenop de sowieso al noodzakelijke
// Firestore-data-ophaling. Dashboard/admin/instellingen hieronder blijven wel
// lazy: die zijn groter, enkel voor ingelogde aanbieders, en minder
// tijdsgevoelig voor de eerste indruk van het merk.
import PublicBookingPage from './components/Booking/PublicBookingPage';
import ManageBookingPage from './components/Booking/ManageBookingPage';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const EmailSettings = lazy(() => import('./components/Settings/EmailSettings'));
const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const EmailVerificationPage = lazy(() => import('./components/Auth/EmailVerificationPage'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
const ZoomCallback = lazy(() => import('./components/ZoomCallback'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const Landing = lazy(() => import('./pages/Landing'));

// Draait de app als geinstalleerde PWA (startscherm-icoon) i.p.v. in een gewoon
// browsertabblad? iOS meldt dat via navigator.standalone, de rest via de
// display-mode-mediaquery. Een geinstalleerde app slaat de landingspagina over.
function isInstalledApp() {
    return window.navigator.standalone === true
        || window.matchMedia('(display-mode: standalone)').matches;
}

const messages = {
    'nl': Dutch,
    'en': English
};

// Alleen / en /dashboard mogen openen op de onthouden sessie (zie AuthContext):
// de rest van de app leest auth.currentUser en userDetails en wacht dus tot
// Firebase de sessie bevestigd en het gebruikersdocument binnen heeft.
function isShellRoute(pathname) {
    return pathname === '/' || pathname === '/dashboard';
}

function PrivateRoute({ children, requiredRole, allowRemembered = false }) {
    const { user, loading, detailsReady, userDetails } = useAuth();
    const intl = useIntl();
    const laadtekst = <div>{intl.formatMessage({ id: 'loading' })}</div>;

    // Doorsturen vervangt de huidige geschiedenisregel (replace). Anders zet elke doorverwijzing
    // een extra regel bij en blijft de terugknop hangen tussen deze pagina en de pagina erna.
    if (loading) {
        return laadtekst;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (!allowRemembered && !detailsReady) {
        return laadtekst;
    }

    if (!user.emailVerified) {
        return <Navigate to="/verify-email" replace />;
    }

    if (requiredRole) {
        // De rol komt enkel uit het gebruikersdocument, nooit uit een onthouden sessie.
        if (!detailsReady) {
            return laadtekst;
        }
        if (userDetails?.role !== requiredRole) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return children;
}

function AppRoutes() {
    const { user, authReady, profiel } = useAuth();
    const location = useLocation();
    // Publieke klant-routes (boeken, boeking zelf beheren, feedback geven)
    // hebben nooit een account nodig — die mogen niet wachten tot Firebase
    // Auth weet of er ergens iemand ingelogd is (irrelevant voor deze
    // bezoekers), dat voegt enkel een onnodige wachtstap toe vóór er iets
    // te zien is. Enkel routes die echt op `user` moeten beslissen (login,
    // dashboard, onboarding, ...) wachten hieronder nog op `authReady`.
    const isPublicCustomerRoute = /^\/(book|manage-booking)\//.test(location.pathname);
    const [language, setLanguage] = useState(() => {
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage) return savedLanguage;

        const browserLanguage = navigator.language || navigator.userLanguage;
        return browserLanguage.startsWith('nl') ? 'nl' : 'en';
    });
    // Onboarding en taal komen uit het profiel van AuthContext (live uit het
    // gebruikersdocument, of uit de startcache zolang Firebase nog niet antwoordde)
    // i.p.v. uit een eigen getDoc. Die las pas ná de aanmelding en liet elke koude
    // start eerst via /onboarding lopen (onboardingCompleted begon op false) voor de
    // Onboarding-pagina terugstuurde naar het dashboard. profiel === null betekent
    // "nog niet bekend": dan wachten we i.p.v. te raden. De lokale vlag dekt het
    // moment waarop Onboarding klaar is en het document nog niet doorkwam.
    const [onboardingLokaalKlaar, setOnboardingCompleted] = useState(false);
    const onboardingCompleted = onboardingLokaalKlaar || profiel?.onboardingCompleted === true;
    const onboardingOnbekend = !!user && !onboardingLokaalKlaar && profiel === null;
    const profielTaal = profiel?.language;
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        if (profielTaal) {
            setLanguage(profielTaal);
        }
    }, [profielTaal]);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem('language', language);
    }, [language]);

    const updateLanguage = (newLanguage) => {
        setLanguage(newLanguage);
    };

    // Teksten die buiten de IntlProvider staan (laden, offline) komen rechtstreeks uit de
    // berichten van de gekozen taal.
    const vertaal = (id) => (messages[language] || messages.nl)[id];
    const laadtekst = vertaal('loading');

    // Wachten tot Firebase de sessie bevestigd heeft, behalve op / en /dashboard met
    // een onthouden sessie. Daar opent de app meteen (zie AuthContext).
    if (!authReady && !(user && isShellRoute(location.pathname)) && !isPublicCustomerRoute) {
        return <div>{laadtekst}</div>;
    }

    if (!isOnline) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                }}>
                    <h2>{vertaal('app.offlineTitle')}</h2>
                    <p>{vertaal('app.offlineText')}</p>
                </div>
            </div>
        );
    }

    return (
        <IntlProvider messages={messages[language]} locale={language}>
            <div>
                <InstallPrompt />
                <Suspense fallback={<div>{laadtekst}</div>}>
                    <Routes>
                        <Route path="/" element={user ? (onboardingOnbekend ? <div>{laadtekst}</div> : onboardingCompleted ? <Navigate to="/dashboard" replace /> : <Navigate to="/onboarding" replace />) : (isInstalledApp() ? <Navigate to="/login" replace /> : <Landing language={language} onLanguageChange={updateLanguage} />)} />
                        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
                        <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/verify-email" element={<EmailVerificationPage />} />
                        <Route path="/book/:customLink" element={<PublicBookingPage />} />
                        <Route path="/manage-booking/:bookingId" element={<ManageBookingPage />} />
                        <Route path="/settings/email" element={<PrivateRoute><EmailSettings /></PrivateRoute>} />
                        <Route path="/dashboard" element={<PrivateRoute allowRemembered>{onboardingOnbekend ? <div>{laadtekst}</div> : onboardingCompleted ? <Dashboard /> : <Navigate to="/onboarding" replace />}</PrivateRoute>} />
                        <Route path="/flexitime-control" element={<PrivateRoute requiredRole="admin"><AdminDashboard /></PrivateRoute>} />
                        <Route path="/onboarding" element={user ? <Onboarding updateLanguage={updateLanguage} setOnboardingCompleted={setOnboardingCompleted} /> : <Navigate to="/login" replace />} />
                        <Route path="/zoomCallback" element={<ZoomCallback />} />
                        <Route path="/upgrade" element={<PrivateRoute><UpgradePage /></PrivateRoute>} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </Suspense>
            </div>
        </IntlProvider>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </Router>
    );
}

export default App;